import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const ACADEMY_STYLES = `
  .acad-hero {
    background: linear-gradient(135deg,#4D4E8D,#41427a);
    color: #fff;
    border-radius: 16px;
    padding: 32px 28px;
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;
  }
  .acad-hero::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(77,78,141,0.15), transparent 70%);
    pointer-events: none;
  }
  .acad-hero-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 32px;
    font-weight: 600;
    color: #4D4E8D;
    margin: 0 0 8px;
    letter-spacing: 0.02em;
  }
  .acad-hero-subtitle {
    font-size: 14px;
    color: rgba(255,255,255,0.85);
    margin: 0;
    line-height: 1.6;
    max-width: 600px;
  }
  .acad-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 20px;
  }
  @media (max-width: 600px) { .acad-stats { grid-template-columns: 1fr; } }
  .acad-stat {
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(77,78,141,0.3);
    border-radius: 12px;
    padding: 14px;
    text-align: center;
  }
  .acad-stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #4D4E8D;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .acad-stat-label {
    font-size: 10px;
    color: rgba(255,255,255,0.7);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-top: 2px;
  }
  .acad-section {
    background: #fff;
    border: 1px solid #E2E4E7;
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 20px;
  }
  .acad-section-header {
    padding: 14px 20px;
    background: linear-gradient(135deg, #F5F6F7, #EEEFF1);
    border-bottom: 1px solid #E2E4E7;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .acad-section-title {
    font-size: 13px;
    font-weight: 600;
    color: #151715;
    letter-spacing: 0.04em;
  }
  .acad-track-card {
    border: 1.5px solid #E2E4E7;
    border-radius: 12px;
    padding: 18px;
    background: #fff;
    transition: all 0.2s;
    cursor: pointer;
  }
  .acad-track-card:hover {
    border-color: #4D4E8D;
    box-shadow: 0 4px 16px rgba(77,78,141,0.12);
    transform: translateY(-2px);
  }
  .acad-track-icon { font-size: 28px; margin-bottom: 10px; }
  .acad-track-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #151715;
    margin: 0 0 6px;
  }
  .acad-track-desc { font-size: 12px; color: #5C6065; line-height: 1.5; margin: 0; }
  .acad-track-meta { display: flex; gap: 12px; margin-top: 10px; font-size: 11px; color: #A2A6AA; flex-wrap: wrap; }

  /* 🆕 PROGRESSIVE MODULE STATES */
  .acad-module-card {
    border: 1px solid #E2E4E7;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 8px;
    background: #fff;
    transition: all 0.2s;
  }
  .acad-module-card.completed {
    border-color: rgba(45,122,79,0.4);
    background: linear-gradient(135deg, #FAFDF5, #fff);
  }
  .acad-module-card.locked {
    border-color: #E2E4E7;
    background: #F7F8F9;
    opacity: 0.75;
  }
  .acad-module-header {
    display: flex; align-items: center; gap: 10px; padding: 12px 14px;
    background: #F5F6F7; cursor: pointer; border: none; width: 100%; text-align: left;
  }
  .acad-module-header.locked { cursor: not-allowed; background: #EEEFF1; }
  .acad-module-header.completed { background: linear-gradient(135deg, #EAF2EE, #F0FAF4); }
  .acad-module-icon {
    width: 32px; height: 32px; border-radius: 8px;
    background: linear-gradient(135deg,#4D4E8D,#41427a);
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; flex-shrink: 0;
  }
  .acad-module-icon.locked {
    background: #A2A6AA;
  }
  .acad-module-icon.completed {
    background: linear-gradient(135deg,#5E8A72,#3A9A60);
  }
  .acad-lesson-item {
    display: flex; align-items: flex-start; gap: 10px; padding: 10px 14px;
    border-bottom: 1px solid #EEEFF1; cursor: pointer; transition: background 0.15s;
  }
  .acad-lesson-item:last-child { border-bottom: none; }
  .acad-lesson-item:hover { background: #F5F6F7; }
  .acad-lesson-content {
    padding: 16px; background: #F5F6F7; font-size: 13px; line-height: 1.8;
    color: #3A3D40; white-space: pre-line; border-top: 1px solid #E2E4E7;
  }
  .acad-quiz-option {
    display: flex; align-items: center; gap: 10px; padding: 10px 14px;
    border: 1.5px solid #E2E4E7; border-radius: 8px; margin-bottom: 8px;
    cursor: pointer; transition: all 0.15s; background: #fff;
    width: 100%; text-align: left; font-family: inherit; font-size: 13px; color: #151715;
  }
  .acad-quiz-option:hover { border-color: #4D4E8D; background: #F5F6F7; }
  .acad-quiz-option.selected { border-color: #4D4E8D; background: #F5F6F7; }
  .acad-empty { text-align: center; padding: 40px 20px; color: #A2A6AA; }
  .acad-empty-icon { font-size: 48px; margin-bottom: 12px; opacity: 0.6; }
  .acad-empty-title {
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px;
    color: #151715; margin: 0 0 6px; font-weight: 600;
  }
  .acad-empty-text { font-size: 13px; color: #5C6065; max-width: 420px; margin: 0 auto 16px; line-height: 1.5; }
  .acad-cta-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  @media (max-width: 600px) { .acad-cta-grid { grid-template-columns: 1fr; } }
  .acad-cta {
    display: flex; align-items: center; gap: 14px; padding: 16px;
    border: 1.5px solid #E2E4E7; border-radius: 12px; background: #fff;
    text-decoration: none; color: inherit; transition: all 0.2s;
  }
  .acad-cta:hover { border-color: #4D4E8D; background: #F5F6F7; transform: translateY(-1px); }
  .acad-cta-icon {
    font-size: 28px; width: 48px; height: 48px;
    background: linear-gradient(135deg, #EEF0F8, #F5E9C8);
    border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .acad-cta-title { font-size: 13px; font-weight: 600; color: #151715; margin: 0; }
  .acad-cta-desc { font-size: 11px; color: #5C6065; margin: 2px 0 0; }

  /* 🆕 FINAL EXAM HERO */
  .acad-final-exam-hero {
    background: linear-gradient(135deg,#4D4E8D,#41427a);
    border: 2px solid #4D4E8D;
    border-radius: 16px;
    padding: 28px 24px;
    margin-bottom: 20px;
    text-align: center;
    color: #fff;
    position: relative;
    overflow: hidden;
  }
  .acad-final-exam-hero::before {
    content: '🏆';
    position: absolute;
    top: -20px; right: -20px;
    font-size: 160px;
    opacity: 0.05;
    transform: rotate(15deg);
  }
  .acad-final-exam-locked {
    background: linear-gradient(135deg, #F5F6F7, #EEEFF1);
    border: 2px dashed #A2A6AA;
    color: #5C6065;
  }
  .acad-final-exam-locked::before {
    content: '🔒';
  }

  /* 🆕 EXAM SECTION CARDS */
  .acad-exam-section-card {
    background: #fff;
    border: 2px solid #E2E4E7;
    border-radius: 14px;
    margin-bottom: 14px;
    overflow: hidden;
    transition: all 0.2s;
  }
  .acad-exam-section-card.active {
    border-color: #4D4E8D;
    box-shadow: 0 4px 16px rgba(77,78,141,0.15);
  }
  .acad-exam-section-card.completed {
    border-color: #5E8A72;
    background: linear-gradient(135deg, #FAFDF5, #fff);
  }
  .acad-exam-section-header {
    padding: 14px 18px;
    background: linear-gradient(135deg, #F5F6F7, #EEF0F8);
    border-bottom: 1px solid #4D4E8D;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .acad-exam-section-number {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #4D4E8D;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 16px;
    flex-shrink: 0;
  }
  .acad-exam-section-number.completed {
    background: #5E8A72;
    color: #fff;
  }
  .acad-progress-bar {
    height: 6px;
    background: #E2E4E7;
    border-radius: 99px;
    overflow: hidden;
  }
  .acad-progress-fill {
    height: 100%;
    background: linear-gradient(90deg,#4D4E8D,#6E70AE);
    border-radius: 99px;
    transition: width 0.4s ease;
  }

  /* CERTIFICATE */
  .cert-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.75); z-index: 1000;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: fadeIn 0.3s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .cert-modal {
    background: #fff;
    border-radius: 20px;
    max-width: 720px;
    width: 100%;
    max-height: 92vh;
    overflow-y: auto;
    animation: slideUp 0.4s ease;
  }
  @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  .cert-paper {
    background: linear-gradient(135deg, #FFFDF5, #FFF8E7);
    border: 3px solid #4D4E8D;
    border-radius: 16px;
    padding: 44px 40px;
    text-align: center;
    position: relative;
    margin: 24px;
  }
  .cert-paper::before {
    content: '';
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(77,78,141,0.3);
    border-radius: 12px;
    pointer-events: none;
  }
`

// ── 🎲 SCRAMBLE HELPER ────────────────────────────────────────
// Fisher-Yates shuffle for fair randomization
function shuffleArray(array) {
  const arr = [...array]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

// Scramble a question's options while tracking the correct answer
function scrambleQuestion(q) {
  const options = []
  if (q.option_a) options.push({ key: 'a', text: q.option_a })
  if (q.option_b) options.push({ key: 'b', text: q.option_b })
  if (q.option_c) options.push({ key: 'c', text: q.option_c })
  if (q.option_d) options.push({ key: 'd', text: q.option_d })

  const shuffled = shuffleArray(options)
  // Find which scrambled position holds the correct answer
  const correctIndex = shuffled.findIndex(opt => opt.key === q.correct_answer)
  const correctLetterScrambled = ['a','b','c','d'][correctIndex]

  return {
    ...q,
    scrambledOptions: shuffled, // [{key:'b', text:'...'}, {key:'a', text:'...'}]
    correctLetterScrambled,      // The new letter (a/b/c/d) of the correct answer
  }
}

// ── 🆕 MODULE QUIZ (with progress tracking) ──────────────────
function ModuleQuiz({ moduleId, trackId, userId, onPassed }) {
  const [questions, setQuestions] = useState([])
  const [scrambled, setScrambled] = useState([])
  const [answers,   setAnswers]   = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [score,     setScore]     = useState(0)
  const [passed,    setPassed]    = useState(false)
  const [attempts,  setAttempts]  = useState(0)
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    loadQuestions()
    loadProgress()
  }, [moduleId])

  async function loadQuestions() {
    setLoading(true)
    // 🛡️ SECURITY: Don't fetch correct_answer to browser — we grade server-side equivalent
    // Actually we need it for grading client-side, but we use scrambled letters
    const { data } = await supabase.from('academy_quiz_questions')
      .select('*')
      .eq('module_id', moduleId)
      .order('sort_order', { ascending: true })
    const qs = data || []
    setQuestions(qs)
    setScrambled(shuffleArray(qs).map(scrambleQuestion))
    setLoading(false)
  }

  async function loadProgress() {
    if (!userId) return
    const { data } = await supabase.from('academy_progress')
      .select('*')
      .eq('notary_id', userId)
      .eq('module_id', moduleId)
      .maybeSingle()
    if (data) {
      setAttempts(data.quiz_attempts || 0)
      if (data.quiz_passed) {
        setPassed(true)
        setSubmitted(true)
        setScore(data.quiz_score || 0)
      }
    }
  }

  async function submit() {
    let correct = 0
    scrambled.forEach(q => {
      if (answers[q.id] === q.correctLetterScrambled) correct++
    })
    const pct = Math.round((correct / scrambled.length) * 100)
    setScore(pct)
    setSubmitted(true)
    const didPass = pct === 100  // 💯 Module quizzes require PERFECT score!!

    // Save progress to database
    const { data: existing } = await supabase.from('academy_progress')
      .select('id, quiz_attempts')
      .eq('notary_id', userId)
      .eq('module_id', moduleId)
      .maybeSingle()

    const newAttempts = (existing?.quiz_attempts || 0) + 1
    setAttempts(newAttempts)

    if (existing) {
      await supabase.from('academy_progress').update({
        quiz_passed:   didPass || existing?.quiz_passed,
        quiz_score:    pct,
        quiz_attempts: newAttempts,
        passed_at:     didPass ? new Date().toISOString() : existing?.passed_at,
      }).eq('id', existing.id)
    } else {
      await supabase.from('academy_progress').insert({
        notary_id:     userId,
        track_id:      trackId,
        module_id:     moduleId,
        quiz_passed:   didPass,
        quiz_score:    pct,
        quiz_attempts: newAttempts,
        passed_at:     didPass ? new Date().toISOString() : null,
      })
    }

    if (didPass) {
      setPassed(true)
      if (onPassed) onPassed(moduleId)
    }
  }

  function retry() {
    setAnswers({})
    setSubmitted(false)
    setScore(0)
    // 🎲 Re-scramble!! Questions AND options shuffle on retake!! 😈
    setScrambled(shuffleArray(questions).map(scrambleQuestion))
  }

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#A2A6AA', fontSize: 12 }}>Loading quiz...</div>
  if (scrambled.length === 0) return <div style={{ padding: 16, fontSize: 12, color: '#A2A6AA', fontStyle: 'italic', textAlign: 'center' }}>No quiz questions in this module yet!!</div>

  if (submitted && passed) return (
    <div style={{ padding: 18, background: '#EAF2EE', borderRadius: 10, margin: '12px 14px', border: '2px solid #5E8A72' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 32 }}>🎉</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A4A2E' }}>Quiz Passed!!</div>
          <div style={{ fontSize: 12, color: '#5E8A72', fontWeight: 600 }}>Module unlocked!!</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#4A6A50' }}>
        ✨ Excellent work!! You may proceed to the next module.
      </div>
    </div>
  )

  if (submitted && !passed) return (
    <div style={{ padding: 18, background: '#EEF0F8', borderRadius: 10, margin: '12px 14px', border: '2px solid #4D4E8D' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 32 }}>📚</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#41427a' }}>Almost there!!</div>
          <div style={{ fontSize: 12, color: '#41427a', fontWeight: 600 }}>Need all answers correct to unlock next module · Attempt #{attempts}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#8A6500', marginBottom: 12, lineHeight: 1.6 }}>
        So close!! For module mastery, we need <strong>ALL answers correct</strong>. Review the lesson content and try again — you've got this!! 💪
      </div>
      <button onClick={retry}
        style={{ padding: '10px 20px', borderRadius: 8, background: 'linear-gradient(135deg,#4D4E8D,#6E70AE)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
        🔄 Retake Quiz
      </button>
    </div>
  )

  return (
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#151715', letterSpacing: '0.06em' }}>
          📝 MODULE QUIZ — {scrambled.length} question{scrambled.length !== 1 ? 's' : ''}
        </div>
        <div style={{ fontSize: 10, color: '#41427a', fontWeight: 600 }}>
          {attempts > 0 ? `Attempt #${attempts + 1}` : 'First attempt'} · 💯 100% to unlock next module
        </div>
      </div>

      {scrambled.map((q, i) => (
        <div key={q.id} style={{ marginBottom: 18, padding: '14px', background: '#F7F8F9', borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#151715', marginBottom: 10, display: 'flex', gap: 8 }}>
            <span style={{ background: '#151715', color: '#4D4E8D', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
              {i+1}
            </span>
            <span style={{ flex: 1 }}>{q.question}</span>
          </div>
          {q.scrambledOptions.map((opt, optIdx) => {
            const letter = ['a','b','c','d'][optIdx]
            const isSelected = answers[q.id] === letter
            return (
              <button key={optIdx} className={`acad-quiz-option${isSelected ? ' selected' : ''}`}
                onClick={() => setAnswers(a => ({ ...a, [q.id]: letter }))}
                type="button">
                <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${isSelected ? '#4D4E8D' : '#E2E4E7'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: isSelected ? '#4D4E8D' : '#A2A6AA', flexShrink: 0 }}>
                  {letter.toUpperCase()}
                </div>
                <span style={{ flex: 1 }}>{opt.text}</span>
              </button>
            )
          })}
        </div>
      ))}

      <button onClick={submit} disabled={Object.keys(answers).length < scrambled.length}
        style={{ width: '100%', padding: '12px', borderRadius: 10, background: Object.keys(answers).length === scrambled.length ? 'linear-gradient(135deg,#5E8A72,#3A9A60)' : '#A2A6AA', color: '#fff', border: 'none', cursor: Object.keys(answers).length === scrambled.length ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14, marginTop: 8 }}>
        Submit Quiz ({Object.keys(answers).length}/{scrambled.length} answered)
      </button>
    </div>
  )
}

// ── 🆕 LESSON QUIZ (per-lesson — friendly, reinforces right after the lesson) ──
// Renders nothing if the lesson has no quiz questions. On a perfect pass it records
// the lesson in academy_lesson_progress and tells the module via onPassed().
function LessonQuiz({ lessonId, moduleId, trackId, userId, onPassed }) {
  const [questions, setQuestions] = useState([])
  const [scrambled, setScrambled] = useState([])
  const [answers,   setAnswers]   = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [score,     setScore]     = useState(0)
  const [passed,    setPassed]    = useState(false)
  const [attempts,  setAttempts]  = useState(0)
  const [loading,   setLoading]   = useState(true)

  useEffect(() => { loadQuestions(); loadProgress() }, [lessonId])

  async function loadQuestions() {
    setLoading(true)
    const { data } = await supabase.from('academy_quiz_questions')
      .select('*')
      .eq('lesson_id', lessonId)
      .order('sort_order', { ascending: true })
    const qs = data || []
    setQuestions(qs)
    setScrambled(shuffleArray(qs).map(scrambleQuestion))
    setLoading(false)
  }

  async function loadProgress() {
    if (!userId) return
    const { data } = await supabase.from('academy_lesson_progress')
      .select('quiz_passed, quiz_score, quiz_attempts')
      .eq('notary_id', userId)
      .eq('lesson_id', lessonId)
      .maybeSingle()
    if (data) {
      setAttempts(data.quiz_attempts || 0)
      if (data.quiz_passed) { setPassed(true); setSubmitted(true); setScore(data.quiz_score || 0) }
    }
  }

  async function submit() {
    let correct = 0
    scrambled.forEach(q => { if (answers[q.id] === q.correctLetterScrambled) correct++ })
    const pct = Math.round((correct / scrambled.length) * 100)
    setScore(pct)
    setSubmitted(true)
    const didPass = pct === 100  // 💯 perfect score, same mastery standard as module quizzes

    const { data: existing } = await supabase.from('academy_lesson_progress')
      .select('id, quiz_attempts, quiz_passed')
      .eq('notary_id', userId)
      .eq('lesson_id', lessonId)
      .maybeSingle()
    const newAttempts = (existing?.quiz_attempts || 0) + 1
    setAttempts(newAttempts)
    const now = new Date().toISOString()

    await supabase.from('academy_lesson_progress').upsert({
      notary_id:     userId,
      lesson_id:     lessonId,
      track_id:      trackId,
      quiz_passed:   didPass || !!existing?.quiz_passed,
      quiz_score:    pct,
      quiz_attempts: newAttempts,
      ...(didPass ? { completed_at: now } : {}),
    }, { onConflict: 'notary_id,lesson_id' })

    if (didPass) { setPassed(true); if (onPassed) onPassed(lessonId) }
  }

  function retry() {
    setAnswers({})
    setSubmitted(false)
    setScore(0)
    setScrambled(shuffleArray(questions).map(scrambleQuestion))
  }

  if (loading) return <div style={{ padding: 14, textAlign: 'center', color: '#A2A6AA', fontSize: 12 }}>Loading quiz...</div>
  if (scrambled.length === 0) return null  // 🔑 lesson has no quiz → show nothing

  if (submitted && passed) return (
    <div style={{ padding: 14, background: '#EAF2EE', borderRadius: 10, margin: '12px 14px', border: '2px solid #5E8A72' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 26 }}>🎉</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1A4A2E' }}>Lesson Quiz Passed!!</div>
          <div style={{ fontSize: 11, color: '#5E8A72', fontWeight: 600 }}>Nice — on to the next lesson!!</div>
        </div>
      </div>
    </div>
  )

  if (submitted && !passed) return (
    <div style={{ padding: 14, background: '#EEF0F8', borderRadius: 10, margin: '12px 14px', border: '2px solid #4D4E8D' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 26 }}>📚</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#41427a' }}>Almost there!!</div>
          <div style={{ fontSize: 11, color: '#41427a', fontWeight: 600 }}>All answers correct to pass · Attempt #{attempts}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#8A6500', marginBottom: 12, lineHeight: 1.6 }}>
        So close!! Give the lesson another quick read and try again — you've got this!! 💪
      </div>
      <button onClick={retry}
        style={{ padding: '9px 18px', borderRadius: 8, background: 'linear-gradient(135deg,#4D4E8D,#6E70AE)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
        🔄 Retake Quiz
      </button>
    </div>
  )

  return (
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#151715', letterSpacing: '0.06em' }}>
          📝 LESSON QUIZ — {scrambled.length} question{scrambled.length !== 1 ? 's' : ''}
        </div>
        <div style={{ fontSize: 10, color: '#41427a', fontWeight: 600 }}>
          {attempts > 0 ? `Attempt #${attempts + 1}` : 'First attempt'} · 💯 100% to pass
        </div>
      </div>

      {scrambled.map((q, i) => (
        <div key={q.id} style={{ marginBottom: 18, padding: '14px', background: '#F7F8F9', borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#151715', marginBottom: 10, display: 'flex', gap: 8 }}>
            <span style={{ background: '#151715', color: '#4D4E8D', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
              {i+1}
            </span>
            <span style={{ flex: 1 }}>{q.question}</span>
          </div>
          {q.scrambledOptions.map((opt, optIdx) => {
            const letter = ['a','b','c','d'][optIdx]
            const isSelected = answers[q.id] === letter
            return (
              <button key={optIdx} className={`acad-quiz-option${isSelected ? ' selected' : ''}`}
                onClick={() => setAnswers(a => ({ ...a, [q.id]: letter }))}
                type="button">
                <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${isSelected ? '#4D4E8D' : '#E2E4E7'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: isSelected ? '#4D4E8D' : '#A2A6AA', flexShrink: 0 }}>
                  {letter.toUpperCase()}
                </div>
                <span style={{ flex: 1 }}>{opt.text}</span>
              </button>
            )
          })}
        </div>
      ))}

      <button onClick={submit} disabled={Object.keys(answers).length < scrambled.length}
        style={{ width: '100%', padding: '12px', borderRadius: 10, background: Object.keys(answers).length === scrambled.length ? 'linear-gradient(135deg,#5E8A72,#3A9A60)' : '#A2A6AA', color: '#fff', border: 'none', cursor: Object.keys(answers).length === scrambled.length ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14, marginTop: 8 }}>
        Submit Quiz ({Object.keys(answers).length}/{scrambled.length} answered)
      </button>
    </div>
  )
}

// ── 🆕 MODULE VIEW (with locked/unlocked/completed states) ──
function ModuleView({ module, moduleIndex, trackId, userId, isLocked, isCompleted, prevModuleTitle, onModulePassed }) {
  const [lessons,        setLessons]        = useState([])
  const [expanded,       setExpanded]       = useState(false)
  const [expandedLesson, setExpandedLesson] = useState(null)
  const [loading,        setLoading]        = useState(false)
  const [showQuiz,       setShowQuiz]       = useState(false)
  const [quizCounts,     setQuizCounts]     = useState({})   // { lessonId: #questions }
  const [passedLessons,  setPassedLessons]  = useState({})   // { lessonId: true }

  async function load() {
    if (isLocked) return
    if (lessons.length > 0) { setExpanded(e => !e); return }
    setLoading(true)
    setExpanded(true)
    const { data } = await supabase.from('academy_lessons')
      .select('*')
      .eq('module_id', module.id)
      .eq('published', true)
      .order('sort_order', { ascending: true })
    const list = data || []
    setLessons(list)
    // 🆕 Per-lesson quiz question counts + this notary's passed lessons
    const ids = list.map(l => l.id)
    if (ids.length) {
      const { data: qq } = await supabase.from('academy_quiz_questions').select('lesson_id').in('lesson_id', ids)
      const counts = {}
      ;(qq || []).forEach(q => { if (q.lesson_id) counts[q.lesson_id] = (counts[q.lesson_id] || 0) + 1 })
      setQuizCounts(counts)
      const { data: lp } = await supabase.from('academy_lesson_progress').select('lesson_id, quiz_passed').eq('notary_id', userId).in('lesson_id', ids)
      const passed = {}
      ;(lp || []).forEach(p => { if (p.quiz_passed) passed[p.lesson_id] = true })
      setPassedLessons(passed)
    }
    setLoading(false)
  }

  // 🆕 When every lesson-with-a-quiz is passed, mark the MODULE complete — this bridges
  // into the existing unlock + final-exam + certificate machinery (which keys off
  // academy_progress.quiz_passed), so nothing downstream had to change.
  async function maybeCompleteModule(passedMap) {
    const quizLessonIds = lessons.filter(l => (quizCounts[l.id] || 0) > 0).map(l => l.id)
    if (quizLessonIds.length === 0 || isCompleted) return
    if (!quizLessonIds.every(id => passedMap[id])) return
    const now = new Date().toISOString()
    const { data: existing } = await supabase.from('academy_progress').select('id').eq('notary_id', userId).eq('module_id', module.id).maybeSingle()
    if (existing) {
      await supabase.from('academy_progress').update({ quiz_passed: true, quiz_score: 100, passed_at: now }).eq('id', existing.id)
    } else {
      await supabase.from('academy_progress').insert({ notary_id: userId, track_id: trackId, module_id: module.id, quiz_passed: true, quiz_score: 100, quiz_attempts: 1, passed_at: now })
    }
    if (onModulePassed) onModulePassed(module.id)
  }

  function handleLessonPassed(lessonId) {
    const next = { ...passedLessons, [lessonId]: true }
    setPassedLessons(next)
    maybeCompleteModule(next)
  }

  const cardClass = isCompleted ? 'completed' : isLocked ? 'locked' : ''
  const headerClass = isCompleted ? 'completed' : isLocked ? 'locked' : ''
  const iconClass = isCompleted ? 'completed' : isLocked ? 'locked' : ''

  return (
    <div className={`acad-module-card ${cardClass}`}>
      <button
        type="button"
        className={`acad-module-header ${headerClass}`}
        onClick={load}
        disabled={isLocked}>
        <div className={`acad-module-icon ${iconClass}`}>
          {isCompleted ? '✓' : isLocked ? '🔒' : (module.icon || '📋')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: isLocked ? '#5C6065' : '#151715' }}>
            Module {moduleIndex + 1}: {module.title}
          </div>
          {isLocked && (
            <div style={{ fontSize: 10, color: '#41427a', marginTop: 2 }}>
              🔒 Pass "{prevModuleTitle}" quiz to unlock
            </div>
          )}
          {isCompleted && (
            <div style={{ fontSize: 10, color: '#5E8A72', marginTop: 2, fontWeight: 600 }}>
              ✓ Completed!!
            </div>
          )}
          {!isLocked && !isCompleted && lessons.length > 0 && (
            <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 1 }}>
              {lessons.length} lesson{lessons.length !== 1 ? 's' : ''} · quiz after each
            </div>
          )}
        </div>
        {!isLocked && <span style={{ color: '#A2A6AA', fontSize: 14 }}>{expanded ? '▲' : '▼'}</span>}
      </button>

      {expanded && !isLocked && (
        <div>
          {loading && <div style={{ padding: 16, fontSize: 12, color: '#A2A6AA', textAlign: 'center' }}>Loading lessons...</div>}
          {!loading && lessons.length === 0 && <div style={{ padding: 16, fontSize: 12, color: '#A2A6AA', textAlign: 'center' }}>No lessons published yet!!</div>}
          {!loading && lessons.map(lesson => {
            const hasQuiz = (quizCounts[lesson.id] || 0) > 0
            const lessonPassed = !!passedLessons[lesson.id]
            const isOpen = expandedLesson === lesson.id
            return (
              <div key={lesson.id}>
                <div className="acad-lesson-item" onClick={() => setExpandedLesson(isOpen ? null : lesson.id)}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: lessonPassed ? '#5E8A72' : (isOpen ? '#151715' : '#EEEFF1'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: (lessonPassed || isOpen) ? '#4D4E8D' : '#5C6065', flexShrink: 0 }}>
                    {lessonPassed ? '✓' : (isOpen ? '▼' : '▶')}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#151715' }}>{lesson.title}</div>
                    {hasQuiz && (
                      <div style={{ fontSize: 10, color: lessonPassed ? '#5E8A72' : '#A2A6AA', fontWeight: 600, marginTop: 2 }}>
                        {lessonPassed ? '✓ Quiz passed' : '📝 Quiz after this lesson'}
                      </div>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div>
                    <div className="acad-lesson-content">{lesson.content}</div>
                    {hasQuiz && (
                      <div style={{ borderTop: '2px solid #4D4E8D', background: '#F5F6F7' }}>
                        <LessonQuiz lessonId={lesson.id} moduleId={module.id} trackId={trackId} userId={userId} onPassed={handleLessonPassed} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* 🆕 MODULE COMPLETION SECTION */}
          {!loading && lessons.length > 0 && (() => {
            const quizLessons = lessons.filter(l => (quizCounts[l.id] || 0) > 0)
            const hasLessonQuizzes = quizLessons.length > 0

            // ── Per-lesson quiz mode (the new friendly flow) ──
            if (hasLessonQuizzes) {
              const passedCount = quizLessons.filter(l => passedLessons[l.id]).length
              return (
                <div style={{ borderTop: '2px solid #4D4E8D', background: '#F5F6F7', padding: 16, textAlign: 'center' }}>
                  {isCompleted ? (
                    <div style={{ color: '#5E8A72' }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>✅</div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>Module Complete!!</div>
                      <div style={{ fontSize: 11, color: '#4A6A50', marginTop: 2 }}>All lesson quizzes passed — great work!!</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#41427a', fontWeight: 600 }}>
                      📝 {passedCount} of {quizLessons.length} lesson quiz{quizLessons.length !== 1 ? 'zes' : ''} passed — finish them all to complete this module!!
                    </div>
                  )}
                </div>
              )
            }

            // ── Legacy module-quiz mode (no per-lesson quizzes) ──
            return (
              <div style={{ borderTop: '2px solid #4D4E8D', background: '#F5F6F7' }}>
                {!showQuiz && !isCompleted && (
                  <div style={{ padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: '#41427a', marginBottom: 12 }}>
                      Ready to test your knowledge of this module??
                    </div>
                    <button onClick={() => setShowQuiz(true)}
                      style={{ padding: '10px 24px', borderRadius: 8, background: 'linear-gradient(135deg,#4D4E8D,#41427a)', color: '#4D4E8D', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                      📝 Take Module Quiz
                    </button>
                  </div>
                )}
                {showQuiz && !isCompleted && (
                  <ModuleQuiz moduleId={module.id} trackId={trackId} userId={userId} onPassed={onModulePassed} />
                )}
                {isCompleted && (
                  <div style={{ padding: 16, textAlign: 'center', color: '#5E8A72' }}>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>✅</div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Module Quiz Passed!!</div>
                    <div style={{ fontSize: 11, color: '#4A6A50', marginTop: 2 }}>Great work — keep going!!</div>
                  </div>
                )}
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}

// ── 🏆 FINAL EXAM (sectioned + scrambled + saved progress!) ──
function FinalExam({ track, userId, onClose, onPassed }) {
  const [sections,    setSections]    = useState([])
  const [allQuestions, setAllQuestions] = useState([])
  const [loading,     setLoading]     = useState(true)
  const [currentSection, setCurrentSection] = useState(0)
  const [answers,     setAnswers]     = useState({})
  const [submitted,   setSubmitted]   = useState(false)
  const [score,       setScore]       = useState(0)
  const [scoreData,   setScoreData]   = useState(null)
  const [attempts,    setAttempts]    = useState(0)

  useEffect(() => { loadExam() }, [])

  async function loadExam() {
    setLoading(true)
    // Load sections in order
    const { data: secs } = await supabase.from('academy_final_exam_sections')
      .select('*')
      .eq('track_id', track.id)
      .order('sort_order', { ascending: true })

    // Load all questions for the track
    const { data: qs } = await supabase.from('academy_final_exam_questions')
      .select('*')
      .eq('track_id', track.id)
      .order('sort_order', { ascending: true })

    // Group questions by section + 🎲 SCRAMBLE within each section
    const sectionsWithQuestions = (secs || []).map(sec => {
      const sectionQs = (qs || []).filter(q => q.section_id === sec.id)
      const scrambledSectionQs = shuffleArray(sectionQs).map(scrambleQuestion)
      return { ...sec, questions: scrambledSectionQs }
    })

    setSections(sectionsWithQuestions)
    setAllQuestions(qs || [])

    // Load existing progress
    const { data: progress } = await supabase.from('academy_final_exam_progress')
      .select('*')
      .eq('notary_id', userId)
      .eq('track_id', track.id)
      .maybeSingle()

    if (progress) {
      setAttempts(progress.attempts || 0)
    }
    setLoading(false)
  }

  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0)
  const answeredCount = Object.keys(answers).length
  const progressPct = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0

  async function submitExam() {
    let correct = 0
    let totalAnswered = 0
    sections.forEach(sec => {
      sec.questions.forEach(q => {
        if (answers[q.id]) {
          totalAnswered++
          if (answers[q.id] === q.correctLetterScrambled) correct++
        }
      })
    })

    const pct = Math.round((correct / totalQuestions) * 100)
    setScore(pct)
    setSubmitted(true)
    const didPass = pct >= 80

    // Per-section breakdown
    const sectionScores = sections.map(sec => {
      let sectionCorrect = 0
      sec.questions.forEach(q => {
        if (answers[q.id] === q.correctLetterScrambled) sectionCorrect++
      })
      return {
        title: sec.title,
        correct: sectionCorrect,
        total: sec.questions.length,
        pct: Math.round((sectionCorrect / sec.questions.length) * 100),
      }
    })
    setScoreData({ correct, total: totalQuestions, pct, sectionScores })

    // Save progress
    const { data: existing } = await supabase.from('academy_final_exam_progress')
      .select('id, attempts')
      .eq('notary_id', userId)
      .eq('track_id', track.id)
      .maybeSingle()

    const newAttempts = (existing?.attempts || 0) + 1
    setAttempts(newAttempts)

    if (existing) {
      await supabase.from('academy_final_exam_progress').update({
        passed:    didPass,
        score:     pct,
        attempts:  newAttempts,
        passed_at: didPass ? new Date().toISOString() : null,
      }).eq('id', existing.id)
    } else {
      await supabase.from('academy_final_exam_progress').insert({
        notary_id: userId,
        track_id:  track.id,
        passed:    didPass,
        score:     pct,
        attempts:  newAttempts,
        passed_at: didPass ? new Date().toISOString() : null,
      })
    }

    if (didPass && onPassed) {
      onPassed({ score: pct, attempts: newAttempts, sectionScores })
    }
  }

  function retry() {
    setAnswers({})
    setSubmitted(false)
    setScore(0)
    setScoreData(null)
    setCurrentSection(0)
    // 🎲 RE-SCRAMBLE EVERYTHING!! Questions AND options!! 😈
    loadExam()
  }

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
      <div style={{ marginTop: 12, color: '#5C6065', fontSize: 13 }}>Loading exam...</div>
    </div>
  )

  if (sections.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#151715', marginBottom: 6 }}>Final Exam Not Ready Yet</div>
      <div style={{ fontSize: 13, color: '#5C6065', marginBottom: 20 }}>The final exam content is being prepared!! Check back soon!!</div>
      <button onClick={onClose} style={{ padding: '10px 24px', borderRadius: 8, background: '#151715', color: '#4D4E8D', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
        ← Back to Modules
      </button>
    </div>
  )

  // RESULTS VIEW
  if (submitted) {
    const didPass = score >= 80
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: didPass ? 'linear-gradient(135deg,#EAF2EE,#F0FAF4)' : 'linear-gradient(135deg,#EEF0F8,#F5F6F7)', border: `3px solid ${didPass ? '#5E8A72' : '#4D4E8D'}`, borderRadius: 16, padding: '32px 24px', textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 64, marginBottom: 12 }}>{didPass ? '🏆' : '📚'}</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 32, fontWeight: 700, color: didPass ? '#1A4A2E' : '#41427a', marginBottom: 6 }}>
            {didPass ? 'CONGRATULATIONS!!' : 'Keep Going!!'}
          </div>
          {didPass ? (
            <div style={{ fontSize: 13, color: '#4A6A50', lineHeight: 1.7, maxWidth: 480, margin: '0 auto' }}>
              You've passed the NSV Final Exam and demonstrated true mastery!! Your <strong>Certificate of Proficiency</strong> is being generated... 🏆✨
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#8A6500', lineHeight: 1.7, maxWidth: 480, margin: '0 auto' }}>
              You need <strong>80% to pass</strong>. Review the material and try again — every attempt makes you stronger!! 💪
            </div>
          )}
        </div>

        {/* SECTION BREAKDOWN */}
        <div style={{ background: '#F7F8F9', border: '1px solid #E2E4E7', borderRadius: 12, padding: 18, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#151715', marginBottom: 12, letterSpacing: '0.04em' }}>
            📊 SECTION-BY-SECTION BREAKDOWN
          </div>
          {scoreData.sectionScores.map((sec, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#151715' }}>{sec.title}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: sec.pct >= 80 ? '#5E8A72' : '#41427a' }}>
                  {sec.correct}/{sec.total}
                </div>
              </div>
              <div className="acad-progress-bar">
                <div className="acad-progress-fill" style={{ width: `${sec.pct}%`, background: sec.pct >= 80 ? 'linear-gradient(90deg,#5E8A72,#3A9A60)' : 'linear-gradient(90deg,#4D4E8D,#6E70AE)' }} />
              </div>
            </div>
          ))}
        </div>

        {/* ACTIONS */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          {!didPass && (
            <button onClick={retry}
              style={{ padding: '12px 28px', borderRadius: 10, background: 'linear-gradient(135deg,#4D4E8D,#6E70AE)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
              🔄 Retake Exam
            </button>
          )}
          <button onClick={onClose}
            style={{ padding: '12px 28px', borderRadius: 10, background: didPass ? 'linear-gradient(135deg,#5E8A72,#3A9A60)' : '#5C6065', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            {didPass ? '🏆 View My Certificate' : '← Back to Modules'}
          </button>
        </div>
      </div>
    )
  }

  // EXAM VIEW
  const currentSec = sections[currentSection]
  const isLastSection = currentSection === sections.length - 1

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* CUSTOM TITLE HERO */}
      {(track.final_exam_title || track.final_exam_intro) && (
        <div style={{ background: 'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius: 14, padding: '24px 20px', marginBottom: 20, textAlign: 'center', border: '2px solid #4D4E8D' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏆</div>
          {track.final_exam_title && (
            <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 6, lineHeight: 1.3 }}>
              {track.final_exam_title}
            </div>
          )}
          {track.final_exam_intro && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
              {track.final_exam_intro}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 10 }}>
            80% to pass · Unlimited retakes {attempts > 0 && `· Attempt #${attempts + 1}`}
          </div>
        </div>
      )}

      {/* PROGRESS BAR */}
      <div style={{ marginBottom: 20, padding: '0 4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#5C6065', fontWeight: 600 }}>
            Section {currentSection + 1} of {sections.length} · {answeredCount}/{totalQuestions} answered
          </span>
          <span style={{ fontSize: 11, color: '#4D4E8D', fontWeight: 700 }}>
            {progressPct}% complete
          </span>
        </div>
        <div className="acad-progress-bar">
          <div className="acad-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* SECTION NAVIGATION TABS */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
        {sections.map((sec, i) => {
          const sectionAnswered = sec.questions.filter(q => answers[q.id]).length
          const isComplete = sectionAnswered === sec.questions.length
          return (
            <button key={i} onClick={() => setCurrentSection(i)}
              style={{
                padding: '8px 14px',
                borderRadius: 20,
                border: `1.5px solid ${currentSection === i ? '#4D4E8D' : isComplete ? '#5E8A72' : '#E2E4E7'}`,
                background: currentSection === i ? '#F5F6F7' : isComplete ? '#EAF2EE' : '#fff',
                color: currentSection === i ? '#41427a' : isComplete ? '#5E8A72' : '#5C6065',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontFamily: 'inherit',
              }}>
              {isComplete && '✓ '}Section {i + 1}
            </button>
          )
        })}
      </div>

      {/* CURRENT SECTION */}
      <div className="acad-exam-section-card active">
        <div className="acad-exam-section-header">
          <div className="acad-exam-section-number">{currentSection + 1}</div>
          <div>
            <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 18, fontWeight: 700, color: '#151715' }}>
              {currentSec.title}
            </div>
            {currentSec.description && (
              <div style={{ fontSize: 11, color: '#41427a', marginTop: 2 }}>{currentSec.description}</div>
            )}
          </div>
        </div>

        <div style={{ padding: '16px 18px' }}>
          {currentSec.questions.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#A2A6AA', fontSize: 12, fontStyle: 'italic' }}>
              No questions in this section yet.
            </div>
          ) : (
            currentSec.questions.map((q, i) => (
              <div key={q.id} style={{ marginBottom: 18, padding: '14px', background: '#F7F8F9', borderRadius: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#151715', marginBottom: 10, display: 'flex', gap: 8 }}>
                  <span style={{ background: '#4D4E8D', color: '#151715', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {i+1}
                  </span>
                  <span style={{ flex: 1, lineHeight: 1.5 }}>{q.question}</span>
                </div>
                {q.scrambledOptions.map((opt, optIdx) => {
                  const letter = ['a','b','c','d'][optIdx]
                  const isSelected = answers[q.id] === letter
                  return (
                    <button key={optIdx} className={`acad-quiz-option${isSelected ? ' selected' : ''}`}
                      onClick={() => setAnswers(a => ({ ...a, [q.id]: letter }))}
                      type="button">
                      <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${isSelected ? '#4D4E8D' : '#E2E4E7'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: isSelected ? '#4D4E8D' : '#A2A6AA', flexShrink: 0 }}>
                        {letter.toUpperCase()}
                      </div>
                      <span style={{ flex: 1 }}>{opt.text}</span>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {/* NAVIGATION */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        {currentSection > 0 && (
          <button onClick={() => setCurrentSection(s => s - 1)}
            style={{ flex: 1, padding: '12px', borderRadius: 10, background: '#F5F6F7', color: '#151715', border: '1px solid #D7DADE', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            ← Previous Section
          </button>
        )}
        {!isLastSection && (
          <button onClick={() => setCurrentSection(s => s + 1)}
            style={{ flex: 1, padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg,#4D4E8D,#41427a)', color: '#4D4E8D', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            Next Section →
          </button>
        )}
        {isLastSection && (
          <button onClick={submitExam} disabled={answeredCount < totalQuestions}
            style={{ flex: 1, padding: '12px', borderRadius: 10, background: answeredCount === totalQuestions ? 'linear-gradient(135deg,#5E8A72,#3A9A60)' : '#A2A6AA', color: '#fff', border: 'none', cursor: answeredCount === totalQuestions ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14 }}>
            {answeredCount === totalQuestions ? '🏆 Submit Final Exam' : `Answer All Questions (${answeredCount}/${totalQuestions})`}
          </button>
        )}
      </div>

      {/* SAVE & CLOSE */}
      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', color: '#5C6065', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>
          💾 Save & Resume Later
        </button>
      </div>
    </div>
  )
}

// ── ⭐ COURSE RATING & FEEDBACK ───────────────────────────────
// Shown on the "Final Exam Passed" hero after a notary earns their certificate.
// Saves to academy_track_reviews (one review per notary per track; editable).
function CourseRating({ trackId, userId, trackTitle }) {
  const [loading,       setLoading]       = useState(true)
  const [saving,        setSaving]        = useState(false)
  const [existing,      setExisting]      = useState(null)
  const [editing,       setEditing]       = useState(false)
  const [rating,        setRating]        = useState(0)
  const [hoverRating,   setHoverRating]   = useState(0)
  const [reviewText,    setReviewText]    = useState('')
  const [wouldRecommend, setWouldRecommend] = useState(true)
  const [justSaved,     setJustSaved]     = useState(false)

  useEffect(() => {
    loadReview()
  }, [trackId, userId])

  async function loadReview() {
    if (!userId || !trackId) { setLoading(false); return }
    setLoading(true)
    const { data } = await supabase.from('academy_track_reviews')
      .select('*')
      .eq('notary_id', userId)
      .eq('track_id', trackId)
      .maybeSingle()
    if (data) {
      setExisting(data)
      setRating(data.rating || 0)
      setReviewText(data.review_text || '')
      setWouldRecommend(data.would_recommend !== false)
    }
    setLoading(false)
  }

  async function submitReview() {
    if (rating < 1) return
    setSaving(true)
    try {
      if (existing) {
        await supabase.from('academy_track_reviews').update({
          rating,
          review_text:     reviewText.trim() || null,
          would_recommend: wouldRecommend,
        }).eq('id', existing.id)
      } else {
        await supabase.from('academy_track_reviews').insert({
          notary_id:       userId,
          track_id:        trackId,
          rating,
          review_text:     reviewText.trim() || null,
          would_recommend: wouldRecommend,
        })
      }
      await loadReview()
      setEditing(false)
      setJustSaved(true)
      setTimeout(() => setJustSaved(false), 2500)
    } catch (err) {
      console.error('[CourseRating] save error:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return null

  const cardStyle = {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(77,78,141,0.3)',
    borderRadius: 14,
    padding: '20px',
    marginTop: 18,
    textAlign: 'center',
  }

  // ── ALREADY REVIEWED (and not editing) → show their rating ──
  if (existing && !editing) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#4D4E8D', letterSpacing: '0.06em', marginBottom: 8 }}>
          {justSaved ? '✅ THANK YOU FOR YOUR FEEDBACK!!' : '⭐ YOUR COURSE RATING'}
        </div>
        <div style={{ fontSize: 26, letterSpacing: 4, marginBottom: 6 }}>
          {'★'.repeat(existing.rating)}<span style={{ color: 'rgba(255,255,255,0.25)' }}>{'★'.repeat(5 - existing.rating)}</span>
        </div>
        {existing.review_text && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontStyle: 'italic', lineHeight: 1.6, maxWidth: 460, margin: '0 auto 8px' }}>
            "{existing.review_text}"
          </div>
        )}
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}>
          {existing.would_recommend ? '👍 You recommend this course to other notaries' : '👎 You would not recommend this course'}
        </div>
        <button onClick={() => setEditing(true)}
          style={{ background: 'none', border: '1px solid rgba(77,78,141,0.4)', color: '#4D4E8D', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 8, fontFamily: 'inherit' }}>
          ✏️ Edit my rating
        </button>
      </div>
    )
  }

  // ── RATING FORM (new review or editing) ──
  const stars = [1, 2, 3, 4, 5]
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.04em', marginBottom: 4 }}>
        ⭐ How was this course?
      </div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 14 }}>
        Your feedback helps us improve the {trackTitle} track for the whole tribe!!
      </div>

      {/* STARS */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
        {stars.map(s => (
          <button key={s}
            type="button"
            onMouseEnter={() => setHoverRating(s)}
            onMouseLeave={() => setHoverRating(0)}
            onClick={() => setRating(s)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 34, lineHeight: 1, padding: 0, color: (hoverRating || rating) >= s ? '#4D4E8D' : 'rgba(255,255,255,0.25)', transition: 'color 0.12s, transform 0.12s', transform: (hoverRating || rating) >= s ? 'scale(1.1)' : 'scale(1)' }}>
            ★
          </button>
        ))}
      </div>

      {/* REVIEW TEXT */}
      <textarea
        value={reviewText}
        onChange={e => setReviewText(e.target.value)}
        placeholder="Share a few words about your experience (optional)..."
        rows={3}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(77,78,141,0.3)', background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box', marginBottom: 14 }}
      />

      {/* WOULD RECOMMEND TOGGLE */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>Would you recommend this course?</span>
        <button type="button" onClick={() => setWouldRecommend(r => !r)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: wouldRecommend ? 'rgba(45,122,79,0.25)' : 'rgba(255,255,255,0.08)', border: `1px solid ${wouldRecommend ? '#3A9A60' : 'rgba(255,255,255,0.25)'}`, color: wouldRecommend ? '#5BC98A' : 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 20, fontFamily: 'inherit' }}>
          {wouldRecommend ? '👍 Yes' : '👎 No'}
        </button>
      </div>

      {/* ACTIONS */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={submitReview} disabled={rating < 1 || saving}
          style={{ padding: '10px 24px', borderRadius: 10, background: rating >= 1 ? 'linear-gradient(135deg,#4D4E8D,#6E70AE)' : 'rgba(255,255,255,0.15)', color: rating >= 1 ? '#151715' : 'rgba(255,255,255,0.5)', border: 'none', cursor: rating >= 1 ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
          {saving ? 'Saving...' : existing ? '💾 Update Rating' : '💾 Submit Rating'}
        </button>
        {existing && (
          <button onClick={() => { setEditing(false); loadReview() }}
            style={{ padding: '10px 18px', borderRadius: 10, background: 'none', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
            Cancel
          </button>
        )}
      </div>
      {rating < 1 && (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 8 }}>
          Tap a star to rate
        </div>
      )}
    </div>
  )
}

// ── 🏆 CERTIFICATE OF PROFICIENCY ────────────────────────────
function ProficiencyCertificate({ track, notaryName, score, completedAt, onClose }) {
  const certNumber = `NSV-EDU-${Date.now().toString().slice(-8)}`

  function printCert() {
    const issued = new Date(completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) + ' · ' + new Date(completedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    const w = window.open('', '_blank')
    w.document.write(`<!DOCTYPE html><html><head><title>NSV Certificate of Proficiency</title><style>
      *{margin:0;padding:0;box-sizing:border-box;}
      html,body{height:100%;}
      body{background:#FFFDF5;display:flex;align-items:center;justify-content:center;font-family:Georgia,serif;padding:16px;}
      .cert{background:linear-gradient(135deg,#FFFDF5,#FFF8E7);border:3px solid #4D4E8D;border-radius:16px;padding:40px 56px;text-align:center;max-width:760px;width:100%;position:relative;}
      .cert::before{content:'';position:absolute;inset:12px;border:1px solid rgba(77,78,141,0.3);border-radius:12px;}
      .badge{width:92px;height:92px;object-fit:contain;margin:0 auto 12px;display:block;}
      .co-name{font-size:13px;letter-spacing:0.18em;color:#41427a;text-transform:uppercase;margin-bottom:4px;font-weight:600;}
      .title{font-size:38px;font-weight:700;color:#151715;margin:12px 0 4px;letter-spacing:0.02em;}
      .subtitle{font-size:18px;color:#41427a;margin-bottom:22px;font-style:italic;}
      .body{font-size:13px;color:#3A3D40;line-height:1.7;}
      .name{font-size:34px;font-weight:600;color:#151715;border-bottom:2px solid #4D4E8D;display:inline-block;padding-bottom:6px;margin:12px 0 18px;font-family:'Plus Jakarta Sans',Georgia,serif;}
      .track-title{font-size:17px;font-weight:700;color:#41427a;margin:6px 0;}
      .sig-img{height:64px;display:block;margin:0 auto -18px;position:relative;z-index:2;}
      .sigs{display:flex;justify-content:center;gap:80px;margin-top:34px;align-items:flex-end;flex-wrap:wrap;}
      .sig-block{text-align:center;min-width:200px;}
      .sig-label{font-size:11px;font-weight:700;color:#41427a;letter-spacing:0.1em;border-top:2px solid #4D4E8D;padding-top:10px;}
      .sig-no-line{font-size:11px;font-weight:700;color:#41427a;letter-spacing:0.1em;padding-top:10px;}
      .sig-sub{font-size:10px;color:#A2A6AA;margin-top:3px;}
      .cert-num{font-size:10px;color:#A2A6AA;letter-spacing:0.12em;margin-top:24px;font-family:monospace;}
      @media print{
        html,body{background:#FFFDF5;padding:0;height:auto;}
        @page{margin:0;size:landscape;}
        .cert{border-radius:0;max-width:none;width:100%;height:100vh;display:flex;flex-direction:column;justify-content:center;padding:32px 60px;page-break-inside:avoid;}
      }
    </style></head><body>
      <div class="cert">
        <img class="badge" src="/lca_education_logo.png" alt="NSV Education Badge" />
        <div class="co-name" style="font-size:14px;letter-spacing:0.14em;">Notary Signing Vault, LLC.</div>
        <div class="co-name" style="font-size:11px;letter-spacing:0.1em;">DBA Nationwide Notary Professionals Network</div>
        <div class="title">Certificate of Proficiency</div>
        <div class="body">This certifies that</div>
        <div class="name">${notaryName}</div>
        <div class="body">has successfully completed the NSV Proficiency Course in</div>
        <div class="track-title">${track.title}</div>
        <div class="body">and has demonstrated mastery through rigorous examination,<br>exemplifying the NSV standard of excellence, integrity,<br>and commitment to professional notary service.</div>
        <div class="sigs">
          <div class="sig-block">
            <img class="sig-img" src="/electronic_signature.png" alt="Nancy Phillips signature" />
            <div class="sig-label">NANCY PHILLIPS</div>
            <div class="sig-sub">Chief Executive Officer & Founder</div>
            <div class="sig-sub">Notary Signing Vault, LLC.</div>
          </div>
          <div class="sig-block">
            <div class="sig-no-line">DATE OF COMPLETION</div>
            <div class="sig-sub">${issued}</div>
          </div>
        </div>
        <div class="cert-num">CERTIFICATE # ${certNumber}</div>
      </div>
      <script>window.onload=function(){window.print()}<\/script>
    </body></html>`)
    w.document.close()
  }

  return (
    <div className="cert-overlay" onClick={onClose}>
      <div className="cert-modal" onClick={e => e.stopPropagation()}>
        <div className="cert-paper">
          <img src="/lca_education_logo.png" alt="NSV Education" style={{ width: 100, height: 100, objectFit: 'contain', marginBottom: 16 }} />
          <div style={{ fontSize: 14, letterSpacing: '0.16em', color: '#41427a', textTransform: 'uppercase', marginBottom: 4, fontWeight: 600 }}>Notary Signing Vault, LLC.</div>
          <div style={{ fontSize: 11, letterSpacing: '0.12em', color: '#41427a', textTransform: 'uppercase', marginBottom: 24 }}>DBA Nationwide Notary Professionals Network</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 36, fontWeight: 700, color: '#151715', marginBottom: 4 }}>Certificate of Proficiency</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 18, color: '#41427a', fontStyle: 'italic', marginBottom: 24 }}>{track.final_exam_title || track.title}</div>

          <div style={{ fontSize: 13, color: '#3A3D40', lineHeight: 1.8, marginBottom: 8 }}>This certifies that</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 32, fontWeight: 600, color: '#151715', borderBottom: '2px solid #4D4E8D', display: 'inline-block', paddingBottom: 4, margin: '8px 0 20px' }}>{notaryName}</div>

          <div style={{ fontSize: 13, color: '#3A3D40', lineHeight: 1.8 }}>has successfully completed the NSV Proficiency Course in</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#41427a', margin: '6px 0' }}>{track.title}</div>
          <div style={{ fontSize: 13, color: '#3A3D40', lineHeight: 1.8, marginBottom: 24 }}>
            and has demonstrated mastery through rigorous examination,<br />exemplifying the NSV standard of excellence, integrity,<br />and commitment to professional notary service.
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 60, marginTop: 28, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <img src="/electronic_signature.png" alt="Nancy Phillips signature" style={{ height: 60, display: 'block', margin: '0 auto -18px', position: 'relative', zIndex: 2 }} />
              <div style={{ borderTop: '2px solid #4D4E8D', paddingTop: 8, fontSize: 11, color: '#41427a', letterSpacing: '0.08em', fontWeight: 600 }}>NANCY PHILLIPS</div>
              <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 2 }}>Chief Executive Officer & Founder</div>
              <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 1 }}>Notary Signing Vault, LLC.</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#41427a', letterSpacing: '0.08em', fontWeight: 600, paddingTop: 10 }}>DATE OF COMPLETION</div>
              <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 2 }}>
                {new Date(completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · {new Date(completedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>

          <div style={{ fontSize: 10, color: '#A2A6AA', letterSpacing: '0.1em', marginTop: 24, fontFamily: 'monospace' }}>CERTIFICATE # {certNumber}</div>
        </div>

        <div style={{ padding: '0 24px 24px', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={onClose} style={{ padding: '12px 28px', borderRadius: 10, background: '#EEEFF1', color: '#151715', border: '1px solid #D7DADE', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Close
          </button>
          <button onClick={printCert} style={{ padding: '12px 28px', borderRadius: 10, background: 'linear-gradient(135deg,#4D4E8D,#41427a)', color: '#4D4E8D', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            🖨️ Print Certificate
          </button>
        </div>
      </div>
    </div>
  )
}

// ── 🆕 TRACK VIEW (with progress, lock logic, final exam) ────
// ── 🆕 TRACK RESOURCES (notary view) ─────────────────────────
// Displays documents that the admin attached to this track via the CMS.
// Read-only — notaries can click to preview/download but cannot edit.
function TrackResources({ trackId }) {
  const [resources, setResources] = useState([])
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        // Get all linked document IDs for this track (with sort_order)
        const { data: links, error: linkErr } = await supabase
          .from('academy_track_resources')
          .select('document_id, sort_order')
          .eq('track_id', trackId)
          .order('sort_order', { ascending: true })
        if (linkErr) throw linkErr

        if (!links || links.length === 0) {
          if (!cancelled) { setResources([]); setLoading(false) }
          return
        }

        // Fetch the actual document records (only published ones)
        const docIds = links.map(l => l.document_id)
        const { data: docs, error: docsErr } = await supabase
          .from('resources_documents')
          .select('*')
          .in('id', docIds)
          .eq('published', true)
        if (docsErr) throw docsErr

        // Preserve admin-defined sort order
        const docsById = Object.fromEntries((docs || []).map(d => [d.id, d]))
        const ordered = links
          .map(l => docsById[l.document_id])
          .filter(Boolean) // drop any unpublished or deleted

        if (!cancelled) setResources(ordered)
      } catch (err) {
        console.error('[TrackResources] Load error:', err)
        if (!cancelled) setResources([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [trackId])

  function fileIcon(fileType) {
    if (!fileType) return '📄'
    const t = fileType.toLowerCase()
    if (t.includes('pdf')) return '📕'
    if (t.includes('word') || t.includes('doc')) return '📘'
    if (t.includes('excel') || t.includes('sheet') || t.includes('xls')) return '📗'
    if (t.includes('image') || t.includes('png') || t.includes('jpg')) return '🖼️'
    return '📄'
  }

  function formatSize(bytes) {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (loading) return null // silent while loading — don't flash an empty card
  if (resources.length === 0) return null // hide entirely if no resources attached

  return (
    <div className="acad-section" style={{ marginBottom: 20 }}>
      <div className="acad-section-header">
        <span className="acad-section-title">📥 Recommended Resources ({resources.length})</span>
        <span style={{ fontSize: 11, color: '#41427a', fontWeight: 600 }}>
          Hand-picked documents for this course
        </span>
      </div>
      <div style={{ padding: 16 }}>
        {resources.map((doc) => (
          <a
            key={doc.id}
            href={doc.file_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '12px 14px',
              background: '#fff',
              border: '1px solid #E2E4E7',
              borderRadius: 10,
              marginBottom: 8,
              textDecoration: 'none',
              color: 'inherit',
              transition: 'all 0.15s',
              cursor: doc.file_url ? 'pointer' : 'default',
            }}
            onMouseEnter={(e) => {
              if (doc.file_url) {
                e.currentTarget.style.borderColor = '#4D4E8D'
                e.currentTarget.style.background = '#F5F6F7'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E2E4E7'
              e.currentTarget.style.background = '#fff'
            }}
          >
            <div style={{ fontSize: 28, flexShrink: 0 }}>{fileIcon(doc.file_type)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#151715', marginBottom: 2 }}>
                {doc.name}
              </div>
              {doc.category && (
                <div style={{ fontSize: 10, color: '#41427a', fontWeight: 600, letterSpacing: '0.04em', marginBottom: 2 }}>
                  {doc.category.toUpperCase()}
                </div>
              )}
              {doc.description && (
                <div style={{ fontSize: 12, color: '#5C6065', lineHeight: 1.4 }}>
                  {doc.description}
                </div>
              )}
              {doc.file_size && (
                <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 4 }}>
                  {formatSize(doc.file_size)}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0, fontSize: 13, color: '#4D4E8D', fontWeight: 700 }}>
              ⬇ Open
            </div>
          </a>
        ))}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// COMPLETION-STYLE CERTIFICATE  (read-and-complete tracks, e.g. the Training Manual)
// Mirrors the original Resources "Certificate of Completion" exactly:
// same shield, gold borders, Nancy's signature, landscape print layout.
// ════════════════════════════════════════════════════════════════════
function CompletionCertificate({ track, notaryName, completedAt, onClose }) {
  function printCert() {
    const issued = completedAt
      ? new Date(completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) + ' · ' + new Date(completedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      : '—'
    const w = window.open('', '_blank')
    w.document.write(`<!DOCTYPE html><html><head><title>NSV Certificate of Completion</title><style>
      *{margin:0;padding:0;box-sizing:border-box;}
      html,body{height:100%;}
      body{background:#FFFDF5;display:flex;align-items:center;justify-content:center;font-family:Georgia,serif;padding:16px;}
      .cert{background:linear-gradient(135deg,#FFFDF5,#FFF8E7);border:3px solid #4D4E8D;border-radius:16px;padding:40px 56px;text-align:center;max-width:760px;width:100%;position:relative;}
      .cert::before{content:'';position:absolute;inset:12px;border:1px solid rgba(77,78,141,0.3);border-radius:12px;}
      .badge{width:92px;height:92px;object-fit:contain;margin:0 auto 12px;display:block;}
      .co-name{font-size:13px;letter-spacing:0.18em;color:#41427a;text-transform:uppercase;margin-bottom:4px;font-weight:600;}
      .title{font-size:38px;font-weight:700;color:#151715;margin:12px 0 4px;letter-spacing:0.02em;}
      .subtitle{font-size:18px;color:#41427a;margin-bottom:22px;font-style:italic;}
      .body{font-size:13px;color:#3A3D40;line-height:1.7;}
      .name{font-size:34px;font-weight:600;color:#151715;border-bottom:2px solid #4D4E8D;display:inline-block;padding-bottom:6px;margin:12px 0 18px;font-family:'Plus Jakarta Sans',Georgia,serif;}
      .sig-img{height:64px;display:block;margin:0 auto -18px;position:relative;z-index:2;}
      .sigs{display:flex;justify-content:center;gap:80px;margin-top:34px;align-items:flex-end;flex-wrap:wrap;}
      .sig-block{text-align:center;min-width:200px;}
      .sig-label{font-size:11px;font-weight:700;color:#41427a;letter-spacing:0.1em;border-top:2px solid #4D4E8D;padding-top:10px;}
      .sig-no-line{font-size:11px;font-weight:700;color:#41427a;letter-spacing:0.1em;padding-top:10px;}
      .sig-sub{font-size:10px;color:#A2A6AA;margin-top:3px;}
      @media print{
        html,body{background:#FFFDF5;padding:0;height:auto;}
        @page{margin:0;size:landscape;}
        .cert{border-radius:0;max-width:none;width:100%;height:100vh;display:flex;flex-direction:column;justify-content:center;padding:32px 60px;page-break-inside:avoid;}
      }
    </style></head><body>
      <div class="cert">
        <img class="badge" src="/lca_education_logo.png" alt="NSV Education Badge" />
        <div class="co-name" style="font-size:14px;letter-spacing:0.14em;">Notary Signing Vault, LLC.</div>
        <div class="co-name" style="font-size:11px;letter-spacing:0.1em;">DBA Nationwide Notary Professionals Network</div>
        <div class="title">Certificate of Completion</div>
        <div class="subtitle">NSV Training Manual</div>
        <div class="body">This certifies that</div>
        <div class="name">${notaryName}</div>
        <div class="body">has successfully completed all chapters of the<br><strong>NSV Notary Professional Training Manual</strong><br>and demonstrated a commitment to excellence, integrity,<br>and the NSV standard of professionalism.</div>
        <div class="sigs">
          <div class="sig-block">
            <img class="sig-img" src="/electronic_signature.png" alt="Nancy Phillips signature" />
            <div class="sig-label">NANCY PHILLIPS</div>
            <div class="sig-sub">Chief Executive Officer & Founder</div>
            <div class="sig-sub">Notary Signing Vault, LLC.</div>
          </div>
          <div class="sig-block">
            <div class="sig-no-line">DATE ISSUED</div>
            <div class="sig-sub">${issued}</div>
          </div>
        </div>
      </div>
      <script>window.onload=function(){window.print()}<\/script>
    </body></html>`)
    w.document.close()
  }

  const issuedDisplay = completedAt
    ? new Date(completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) + ' · ' + new Date(completedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : '—'

  return (
    <div className="cert-overlay" onClick={onClose}>
      <div className="cert-modal" onClick={e => e.stopPropagation()}>
        <div className="cert-paper">
          <img src="/lca_education_logo.png" alt="NSV Education" style={{ width: 100, height: 100, objectFit: 'contain', marginBottom: 16 }} />
          <div style={{ fontSize: 14, letterSpacing: '0.16em', color: '#41427a', textTransform: 'uppercase', marginBottom: 4, fontWeight: 600 }}>Notary Signing Vault, LLC.</div>
          <div style={{ fontSize: 11, letterSpacing: '0.12em', color: '#41427a', textTransform: 'uppercase', marginBottom: 24 }}>DBA Nationwide Notary Professionals Network</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 36, fontWeight: 700, color: '#151715', marginBottom: 4 }}>Certificate of Completion</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 18, color: '#41427a', fontStyle: 'italic', marginBottom: 24 }}>NSV Training Manual</div>

          <div style={{ fontSize: 13, color: '#3A3D40', lineHeight: 1.8, marginBottom: 8 }}>This certifies that</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 32, fontWeight: 600, color: '#151715', borderBottom: '2px solid #4D4E8D', display: 'inline-block', paddingBottom: 4, margin: '8px 0 20px' }}>{notaryName}</div>

          <div style={{ fontSize: 13, color: '#3A3D40', lineHeight: 1.8 }}>
            has successfully completed all chapters of the<br /><strong>NSV Notary Professional Training Manual</strong><br />and demonstrated a commitment to excellence, integrity,<br />and the NSV standard of professionalism.
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 60, marginTop: 32, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <img src="/electronic_signature.png" alt="Nancy Phillips signature" style={{ height: 60, display: 'block', margin: '0 auto -16px', position: 'relative', zIndex: 2 }} />
              <div style={{ borderTop: '2px solid #4D4E8D', paddingTop: 8, fontSize: 11, color: '#41427a', letterSpacing: '0.08em', fontWeight: 600 }}>NANCY PHILLIPS</div>
              <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 2 }}>Chief Executive Officer & Founder</div>
              <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 1 }}>Notary Signing Vault, LLC.</div>
            </div>
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <div style={{ fontSize: 11, color: '#41427a', letterSpacing: '0.08em', fontWeight: 600 }}>DATE ISSUED</div>
              <div style={{ fontSize: 10, color: '#A2A6AA', marginTop: 2 }}>{issuedDisplay}</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '16px 24px', textAlign: 'center', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button style={{ padding: '12px 32px', borderRadius: 10, border: '1px solid #D7DADE', background: '#EEEFF1', color: '#151715', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }} onClick={onClose}>Close</button>
          <button style={{ padding: '12px 32px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#4D4E8D,#41427a)', color: '#4D4E8D', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }} onClick={printCert}>🖨️ Print Certificate</button>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// COMPLETION-STYLE TRACK VIEW  (read each lesson → mark complete → certificate)
// Used ONLY for tracks flagged completion_mode = true (e.g. the Training Manual).
// Exam-style tracks continue to use <TrackView> below and are 100% unaffected.
// ════════════════════════════════════════════════════════════════════
function CompletionTrackView({ track, onBack, userId, notaryName }) {
  const [lessons,        setLessons]        = useState([])
  const [completed,      setCompleted]      = useState({}) // { lessonId: completed_at }
  const [expandedLesson, setExpandedLesson] = useState(null)
  const [loading,        setLoading]        = useState(true)
  const [showCert,       setShowCert]       = useState(false)
  const [busyLesson,     setBusyLesson]     = useState(null)

  useEffect(() => {
    loadAll()
  }, [track.id])

  async function loadAll() {
    setLoading(true)
    const { data: les } = await supabase.from('academy_lessons')
      .select('*')
      .eq('track_id', track.id)
      .eq('published', true)
      .order('sort_order', { ascending: true })
    setLessons(les || [])

    const { data: prog } = await supabase.from('academy_lesson_progress')
      .select('lesson_id, completed_at')
      .eq('notary_id', userId)
      .eq('track_id', track.id)
    const map = {}
    ;(prog || []).forEach(p => { map[p.lesson_id] = p.completed_at })
    setCompleted(map)
    setLoading(false)
  }

  async function toggleComplete(lessonId) {
    if (busyLesson) return
    setBusyLesson(lessonId)
    if (completed[lessonId]) {
      // Un-mark
      const prevVal = completed[lessonId]
      setCompleted(c => { const n = { ...c }; delete n[lessonId]; return n })
      const { error } = await supabase.from('academy_lesson_progress')
        .delete()
        .eq('notary_id', userId)
        .eq('lesson_id', lessonId)
      if (error) { setCompleted(c => ({ ...c, [lessonId]: prevVal })); alert('Could not update your progress — please try again!!') }
    } else {
      // Mark complete
      const now = new Date().toISOString()
      setCompleted(c => ({ ...c, [lessonId]: now }))
      const { error } = await supabase.from('academy_lesson_progress')
        .upsert(
          { notary_id: userId, lesson_id: lessonId, track_id: track.id, completed_at: now },
          { onConflict: 'notary_id,lesson_id' }
        )
      if (error) { setCompleted(c => { const n = { ...c }; delete n[lessonId]; return n }); alert('Could not save your progress — please try again!!') }
    }
    setBusyLesson(null)
  }

  const completedCount = lessons.filter(l => completed[l.id]).length
  const total = lessons.length
  const allComplete = total > 0 && completedCount === total
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0
  const certDate = allComplete
    ? lessons.map(l => completed[l.id]).filter(Boolean).sort().slice(-1)[0]
    : null

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
      <div style={{ marginTop: 12, color: '#5C6065', fontSize: 13 }}>Loading your Training Manual...</div>
    </div>
  )

  return (
    <>
      {showCert && (
        <CompletionCertificate track={track} notaryName={notaryName} completedAt={certDate} onClose={() => setShowCert(false)} />
      )}

      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#4D4E8D', fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        ← Back to Tracks
      </button>

      {/* TRACK HEADER */}
      <div style={{ background: 'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>{track.icon || '📋'}</div>
        <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 22, color: '#fff', fontWeight: 600, marginBottom: 6 }}>{track.title}</div>
        {track.subtitle && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>{track.subtitle}</div>}
        {track.description && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 14 }}>{track.description}</div>}
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{completedCount} of {total} lessons complete</span>
            <span style={{ fontSize: 11, color: '#fff', fontWeight: 700 }}>{pct}%</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: allComplete ? '#5E8A72' : 'linear-gradient(90deg,#4D4E8D,#6E70AE)', borderRadius: 99, width: `${pct}%`, transition: 'width 0.5s' }} />
          </div>
        </div>
      </div>

      {/* LESSONS */}
      <div className="acad-section">
        <div className="acad-section-header">
          <span className="acad-section-title">📖 Lessons ({total})</span>
          <span style={{ fontSize: 11, color: allComplete ? '#5E8A72' : '#A2A6AA', fontWeight: 600 }}>
            {allComplete ? '✓ All complete!!' : `${completedCount}/${total} done`}
          </span>
        </div>
        <div style={{ padding: 16 }}>
          {total === 0 && <div style={{ textAlign: 'center', padding: 24, color: '#A2A6AA', fontSize: 13 }}>No lessons published yet!!</div>}
          {lessons.map((lesson, idx) => {
            const isDone = !!completed[lesson.id]
            const isOpen = expandedLesson === lesson.id
            return (
              <div key={lesson.id} className="acad-module-card" style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => setExpandedLesson(isOpen ? null : lesson.id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: isDone ? '#5E8A72' : '#EEEFF1', color: isDone ? '#fff' : '#5C6065', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                    {isDone ? '✓' : idx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#151715' }}>{lesson.title}</div>
                    {isDone && <div style={{ fontSize: 10, color: '#5E8A72', marginTop: 2, fontWeight: 600 }}>✓ Completed!!</div>}
                  </div>
                  <span style={{ color: '#A2A6AA', fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
                </button>
                {isOpen && (
                  <div>
                    <div className="acad-lesson-content">{lesson.content}</div>
                    <div style={{ padding: 16, borderTop: '1px solid #EEEFF1', textAlign: 'center' }}>
                      <button
                        onClick={() => toggleComplete(lesson.id)}
                        disabled={busyLesson === lesson.id}
                        style={{
                          padding: '10px 24px', borderRadius: 8, border: isDone ? '2px solid #5E8A72' : 'none',
                          background: isDone ? 'transparent' : 'linear-gradient(135deg,#4D4E8D,#41427a)',
                          color: isDone ? '#5E8A72' : '#4D4E8D', cursor: busyLesson === lesson.id ? 'wait' : 'pointer',
                          fontWeight: 700, fontSize: 13, fontFamily: 'inherit', opacity: busyLesson === lesson.id ? 0.6 : 1
                        }}>
                        {isDone ? '✓ Completed — tap to undo' : '✓ Mark This Lesson Complete'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* CERTIFICATE HERO */}
      <div className={`acad-final-exam-hero ${!allComplete ? 'acad-final-exam-locked' : ''}`}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>{allComplete ? '🎓' : '🔒'}</div>
        {allComplete ? (
          <>
            <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 26, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
              You've Completed the Training Manual!!
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, maxWidth: 520, margin: '0 auto 6px' }}>
              🎉 Congratulations!! You've read and completed all {total} lessons of the NSV Notary Professional Training Manual.
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', marginBottom: 18 }}>
              I'm so proud of you. Welcome to the standard we hold!! 💜 — Your Friendly CEO
            </div>
            <button onClick={() => setShowCert(true)}
              style={{ padding: '14px 32px', borderRadius: 10, background: 'linear-gradient(135deg,#4D4E8D,#6E70AE)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
              🎓 View My Certificate of Completion
            </button>
          </>
        ) : (
          <>
            <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
              Your Certificate Awaits
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
              Read and complete all {total} lessons to unlock your Certificate of Completion. You're {completedCount} of {total} there — keep going!!
            </div>
          </>
        )}
      </div>
    </>
  )
}

function TrackView({ track, onBack, userId, notaryName }) {
  const [modules,         setModules]         = useState([])
  const [loading,         setLoading]         = useState(true)
  const [progress,        setProgress]        = useState({}) // { moduleId: { quiz_passed: bool } }
  const [showFinalExam,   setShowFinalExam]   = useState(false)
  const [finalExamPassed, setFinalExamPassed] = useState(false)
  const [showCertificate, setShowCertificate] = useState(false)
  const [examScore,       setExamScore]       = useState(null)
  const [examCompletedAt, setExamCompletedAt] = useState(null)
  const [hasFinalExamQuestions, setHasFinalExamQuestions] = useState(false)

  useEffect(() => {
    loadAll()
  }, [track.id])

  async function loadAll() {
    setLoading(true)
    // Load modules
    const { data: mods } = await supabase.from('academy_modules')
      .select('*')
      .eq('track_id', track.id)
      .eq('published', true)
      .order('sort_order', { ascending: true })
    setModules(mods || [])

    // Load progress for all modules
    const { data: progressData } = await supabase.from('academy_progress')
      .select('*')
      .eq('notary_id', userId)
      .eq('track_id', track.id)
    const progressMap = {}
    ;(progressData || []).forEach(p => { progressMap[p.module_id] = p })
    setProgress(progressMap)

    // Check final exam progress
    const { data: examProgress } = await supabase.from('academy_final_exam_progress')
      .select('*')
      .eq('notary_id', userId)
      .eq('track_id', track.id)
      .maybeSingle()
    if (examProgress?.passed) {
      setFinalExamPassed(true)
      setExamScore(examProgress.score)
      setExamCompletedAt(examProgress.passed_at)
    }

    // Check if final exam has questions
    const { count } = await supabase.from('academy_final_exam_questions')
      .select('id', { count: 'exact', head: true })
      .eq('track_id', track.id)
    setHasFinalExamQuestions((count || 0) > 0)

    setLoading(false)
  }

  const handleModulePassed = useCallback((moduleId) => {
    // Refresh progress when a module is passed
    setProgress(prev => ({ ...prev, [moduleId]: { ...prev[moduleId], quiz_passed: true } }))
  }, [])

  // Calculate which modules are completed
  function isModuleCompleted(moduleId) {
    return progress[moduleId]?.quiz_passed === true
  }

  // Calculate which modules are locked
  function isModuleLocked(moduleIndex) {
    if (moduleIndex === 0) return false // First module always unlocked
    const prevModule = modules[moduleIndex - 1]
    return !isModuleCompleted(prevModule.id)
  }

  // Stats
  const completedModulesCount = modules.filter(m => isModuleCompleted(m.id)).length
  const allModulesCompleted = modules.length > 0 && completedModulesCount === modules.length
  const trackPct = modules.length > 0 ? Math.round((completedModulesCount / modules.length) * 100) : 0

  function handleExamPassed(result) {
    setFinalExamPassed(true)
    setExamScore(result.score)
    setExamCompletedAt(new Date().toISOString())
    setShowFinalExam(false)
    setTimeout(() => setShowCertificate(true), 600)
  }

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
      <div style={{ marginTop: 12, color: '#5C6065', fontSize: 13 }}>Loading track...</div>
    </div>
  )

  // FINAL EXAM VIEW
  if (showFinalExam) {
    return (
      <>
        <button onClick={() => setShowFinalExam(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#4D4E8D', fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back to Modules
        </button>
        <FinalExam track={track} userId={userId} onClose={() => setShowFinalExam(false)} onPassed={handleExamPassed} />
      </>
    )
  }

  return (
    <>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#4D4E8D', fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        ← Back to Tracks
      </button>

      {/* TRACK HEADER */}
      <div style={{ background: 'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>{track.icon || '🎓'}</div>
        <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 22, color: '#fff', fontWeight: 600, marginBottom: 6 }}>{track.title}</div>
        {track.subtitle && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>{track.subtitle}</div>}
        {track.description && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 14 }}>{track.description}</div>}

        {/* PROGRESS BAR */}
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
              {completedModulesCount} of {modules.length} modules complete
            </span>
            <span style={{ fontSize: 11, color: '#fff', fontWeight: 700 }}>{trackPct}%</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: finalExamPassed ? '#5E8A72' : 'linear-gradient(90deg,#4D4E8D,#6E70AE)', borderRadius: 99, width: `${trackPct}%`, transition: 'width 0.5s' }} />
          </div>
        </div>
      </div>

      {/* MODULES */}
      <div className="acad-section">
        <div className="acad-section-header">
          <span className="acad-section-title">📦 Modules ({modules.length})</span>
          <span style={{ fontSize: 11, color: completedModulesCount === modules.length ? '#5E8A72' : '#A2A6AA', fontWeight: 600 }}>
            {completedModulesCount === modules.length ? '✓ All complete!!' : `${completedModulesCount}/${modules.length} done`}
          </span>
        </div>
        <div style={{ padding: 16 }}>
          {modules.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: '#A2A6AA', fontSize: 13 }}>No published modules in this track yet!!</div>}
          {modules.map((mod, idx) => (
            <ModuleView
              key={mod.id}
              module={mod}
              moduleIndex={idx}
              trackId={track.id}
              userId={userId}
              isLocked={isModuleLocked(idx)}
              isCompleted={isModuleCompleted(mod.id)}
              prevModuleTitle={idx > 0 ? modules[idx-1].title : null}
              onModulePassed={handleModulePassed}
            />
          ))}
        </div>
      </div>

      {/* 📥 RECOMMENDED RESOURCES — hidden if track has none attached */}
      <TrackResources trackId={track.id} />

      {/* 🏆 FINAL EXAM SECTION */}
      {hasFinalExamQuestions && (
        <div className={`acad-final-exam-hero ${!allModulesCompleted ? 'acad-final-exam-locked' : ''}`}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>{finalExamPassed ? '🏆' : allModulesCompleted ? '📝' : '🔒'}</div>

          {finalExamPassed ? (
            <>
              <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 26, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                Final Exam Passed!!
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginBottom: 18, lineHeight: 1.6 }}>
                🎉 Congratulations!! You've mastered the {track.final_exam_title || 'Final Exam'} and earned your Certificate of Proficiency!!
              </div>
              <button onClick={() => setShowCertificate(true)}
                style={{ padding: '14px 32px', borderRadius: 10, background: 'linear-gradient(135deg,#4D4E8D,#6E70AE)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
                🏆 View My Certificate of Proficiency
              </button>

              {/* ⭐ COURSE RATING — appears after earning certificate */}
              <CourseRating trackId={track.id} userId={userId} trackTitle={track.title} />
            </>
          ) : allModulesCompleted ? (
            <>
              <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 26, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                {track.final_exam_title || 'Final Exam'}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginBottom: 18, lineHeight: 1.6, maxWidth: 500, margin: '0 auto 18px' }}>
                {track.final_exam_intro || 'Test your mastery of all the modules in this track. Pass with 80% to earn your Certificate of Proficiency!!'}
              </div>
              <button onClick={() => setShowFinalExam(true)}
                style={{ padding: '14px 32px', borderRadius: 10, background: 'linear-gradient(135deg,#4D4E8D,#6E70AE)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
                🏆 Take the Final Exam
              </button>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 12 }}>
                80% to pass · Unlimited retakes
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: 'Plus Jakarta Sans,sans-serif', fontSize: 26, fontWeight: 700, color: '#5C6065', marginBottom: 8 }}>
                Final Exam Locked
              </div>
              <div style={{ fontSize: 13, color: '#5C6065', marginBottom: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto 14px' }}>
                Complete all {modules.length} modules to unlock the {track.final_exam_title || 'Final Exam'} and earn your Certificate of Proficiency!!
              </div>
              <div style={{ fontSize: 12, color: '#41427a', fontWeight: 600 }}>
                {modules.length - completedModulesCount} module{modules.length - completedModulesCount !== 1 ? 's' : ''} remaining
              </div>
            </>
          )}
        </div>
      )}

      {/* CERTIFICATE MODAL */}
      {showCertificate && examCompletedAt && (
        <ProficiencyCertificate
          track={track}
          notaryName={notaryName}
          score={examScore}
          completedAt={examCompletedAt}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </>
  )
}

// ── MAIN ACADEMY ──────────────────────────────────────────────
export default function NotaryAcademy() {
  const { user, profile } = useAuth()
  const [tracks,        setTracks]        = useState([])
  const [loading,       setLoading]       = useState(true)
  const [selectedTrack, setSelectedTrack] = useState(null)
  const [completedCount, setCompletedCount] = useState(0)

  useEffect(() => {
    supabase.from('academy_tracks')
      .select('*')
      .eq('published', true)
      .order('sort_order', { ascending: true })
      .then(({ data, error }) => {
        if (!error && data) setTracks(data)
        setLoading(false)
      })

    // Count completed tracks (where final exam passed)
    if (user) {
      supabase.from('academy_final_exam_progress')
        .select('id', { count: 'exact', head: true })
        .eq('notary_id', user.id)
        .eq('passed', true)
        .then(({ count }) => setCompletedCount(count || 0))
    }
  }, [user])

  if (selectedTrack) return (
    <>
      <style>{ACADEMY_STYLES}</style>
      {selectedTrack.completion_mode ? (
        <CompletionTrackView
          track={selectedTrack}
          onBack={() => setSelectedTrack(null)}
          userId={user?.id}
          notaryName={profile?.full_name || 'Notary Professional'}
        />
      ) : (
        <TrackView
          track={selectedTrack}
          onBack={() => setSelectedTrack(null)}
          userId={user?.id}
          notaryName={profile?.full_name || 'Notary Professional'}
        />
      )}
    </>
  )

  return (
    <>
      <style>{ACADEMY_STYLES}</style>

      {/* HERO */}
      <div className="acad-hero">
        <h1 className="acad-hero-title">🎓 NSV Notary Education</h1>
        <p className="acad-hero-subtitle">
          Welcome to your professional development hub, {profile?.full_name?.split(' ')[0] || 'Notary'}.
          Sharpen your skills, earn certifications, and unlock higher-paying signings.
        </p>
        <div className="acad-stats">
          <div className="acad-stat"><div className="acad-stat-value">{tracks.length}</div><div className="acad-stat-label">Tracks Available</div></div>
          <div className="acad-stat"><div className="acad-stat-value">{completedCount}</div><div className="acad-stat-label">Completed</div></div>
          <div className="acad-stat"><div className="acad-stat-value">∞</div><div className="acad-stat-label">Growth Potential</div></div>
        </div>
      </div>

      {/* TRAINING TRACKS */}
      <div className="acad-section">
        <div className="acad-section-header">
          <span className="acad-section-title">📚 Training Tracks</span>
          {tracks.length > 0 && <span style={{ fontSize: 11, color: '#A2A6AA' }}>{tracks.length} available</span>}
        </div>
        <div style={{ padding: 20 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#A2A6AA', fontSize: 13 }}>Loading tracks...</div>
          ) : tracks.length === 0 ? (
            <div className="acad-empty">
              <div className="acad-empty-icon">🌱</div>
              <h3 className="acad-empty-title">Education Tracks Coming Soon!!</h3>
              <p className="acad-empty-text">We are curating world-class training tracks for NSV notaries. Check back soon!!</p>
              <p style={{ fontSize: 12, color: '#41427a', fontStyle: 'italic' }}>In the meantime, explore the Notary Resources for state-specific guides!!</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {tracks.map(track => (
                <div key={track.id} className="acad-track-card" onClick={() => setSelectedTrack(track)}>
                  <div className="acad-track-icon">{track.icon || '🎓'}</div>
                  <h3 className="acad-track-title">{track.title}</h3>
                  {track.subtitle && <p style={{ fontSize: 11, color: '#41427a', fontWeight: 600, margin: '0 0 4px' }}>{track.subtitle}</p>}
                  <p className="acad-track-desc">{track.description || 'Professional training track'}</p>
                  <div className="acad-track-meta"><span>📖 Tap to explore →</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* QUICK ACCESS */}
      <div className="acad-section">
        <div className="acad-section-header"><span className="acad-section-title">🚀 Quick Access</span></div>
        <div style={{ padding: 20 }}>
          <div className="acad-cta-grid">
            <Link to="/notary/resources" className="acad-cta"><div className="acad-cta-icon">📚</div><div><p className="acad-cta-title">Notary Resources</p><p className="acad-cta-desc">State rules, training manual, glossary</p></div></Link>
            <Link to="/notary/credentials" className="acad-cta"><div className="acad-cta-icon">🛡️</div><div><p className="acad-cta-title">My Credentials</p><p className="acad-cta-desc">Manage commission, E&O, certifications</p></div></Link>
            <Link to="/notary/rewards" className="acad-cta"><div className="acad-cta-icon">🏆</div><div><p className="acad-cta-title">My Rewards</p><p className="acad-cta-desc">Tier progression, perks, achievements</p></div></Link>
            <Link to="/notary/profile" className="acad-cta"><div className="acad-cta-icon">👤</div><div><p className="acad-cta-title">My Profile</p><p className="acad-cta-desc">Your public NSV business card</p></div></Link>
          </div>
        </div>
      </div>

      {/* WHY ACADEMY MATTERS */}
      <div className="acad-section">
        <div className="acad-section-header"><span className="acad-section-title">💎 Why Education Matters</span></div>
        <div style={{ padding: 20, fontSize: 13, color: '#3A3D40', lineHeight: 1.7 }}>
          <p style={{ margin: '0 0 12px' }}><strong style={{ color: '#151715' }}>Our mission:</strong> Transform our network from "people with stamps" into elite, NSV-certified professionals trusted by the nation's top Title Companies.</p>
          <p style={{ margin: '0 0 12px' }}>Each completed track unlocks <strong style={{ color: '#41427a' }}>priority job access</strong>, higher-paying signings, and exclusive perks reserved for our most committed notaries.</p>
          <p style={{ margin: 0, color: '#5C6065', fontSize: 12, fontStyle: 'italic' }}>"When you grow, we all grow." — Nancy Phillips, Your Friendly CEO</p>
        </div>
      </div>
    </>
  )
}
